import * as React from "react"
import { Heading, HeadingProps, Notification, Text } from "gatsby-interface"
import { ThemeCss, Button } from "gatsby-interface"
import { errorAlert as errorAlertText } from "@modules/locales/default.js"
import { useTriggerZendeskWidget } from "@modules/zendesk"

const baseCss: ThemeCss = theme => ({
  boxShadow: `0 0 0 1px ${theme.tones.DANGER.light} inset`,
  marginBottom: theme.space[5],
})

const headlineCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontWeight: theme.fontWeights.bold,
  fontSize: theme.fontSizes[1],
  marginTop: 0,
  marginBottom: theme.space[3],
})

const supportSeparatorCss: ThemeCss = theme => ({
  backgroundColor: theme.colors.grey[30],
  marginTop: theme.space[5],
  marginBottom: theme.space[5],
})

const supportTextCss: ThemeCss = _theme => ({
  color: `inherit`,
  fontSize: `inherit`,
  margin: 0,
})

const contactButton: ThemeCss = theme => ({
  marginTop: 10,
})

export type ErrorAlertProps = {
  headline?: React.ReactNode
  headlineAs?: HeadingProps["as"]
  children?: React.ReactNode
  includeSupport?: boolean
  className?: string
}

export function ErrorAlert({
  headline = errorAlertText.headers.defaultHeader,
  headlineAs = `h3`,
  children,
  includeSupport = true,
  className,
}: ErrorAlertProps) {
  const triggerZendeskWidget = useTriggerZendeskWidget()

  return (
    <Notification
      content={
        <div>
          <Heading css={headlineCss} as={headlineAs}>
            {headline}
          </Heading>
          {children}
          {includeSupport && (
            <React.Fragment>
              <hr css={supportSeparatorCss} />
              <Text css={supportTextCss}>
                {errorAlertText.messages.supportIntro}
              </Text>
              <Text css={supportTextCss}>
                {errorAlertText.messages.supportContact}
              </Text>
              <Button
                size="M"
                onClick={() => {
                  triggerZendeskWidget()
                }}
                css={contactButton}
              >
                {errorAlertText.actions.contactSupport}
              </Button>
            </React.Fragment>
          )}
        </div>
      }
      variant="SECONDARY"
      tone="DANGER"
      role="alert"
      className={className}
      css={baseCss}
    />
  )
}

export function useTriggerErrorAlert<T extends { message: string }>() {
  const [error, setError] = React.useState<T | null>(null)

  const errorAlert = error ? <ErrorAlert>{error.message}</ErrorAlert> : null

  return [setError, errorAlert, error] as const
}
